import React, { useCallback } from 'react'
import { Box, styled, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
  FilterForm,
  TopToolbar,
  useCreatePath,
  useRecordContext,
  useResourceContext,
  useTranslate,
} from 'react-admin'
import { useNavigate } from 'react-router-dom'
// Icons
import ContentAdd from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/GetApp'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import ImageEye from '@mui/icons-material/RemoveRedEye'
import ActionList from '@mui/icons-material/List'
import ContentCreate from '@mui/icons-material/Create'

// Buttons
import {
  CreateButton,
  EditButton,
  ExportButton,
  FilterButton,
  MenuIconButton,
  PreviewButton,
  ShowButton,
  ListButton,
  BackIconButton,
} from '../buttons'
import { useExport } from '../../hooks/useExport'
import { usePreview } from '../../hooks/usePreview'
import { IListActions, IMenuItem } from './type'

const TopToolbarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing(5),
  padding: theme.spacing(2),
  [theme.breakpoints.up('xs')]: {
    padding: `${theme.spacing(2)} ${theme.spacing(2)} 0`,
  },
  '& .RaFilterForm-root': {
    alignItems: 'center',
  },
  '& .filter-field': {
    marginBottom: 0,
    alignItems: 'center',
    '& .RaFilterFormInput-hideButton': {
      marginBottom: 0,
    },
    '& .ra-input': {
      [theme.breakpoints.up('md')]: {
        margin: 0,
      },
    },
  },
}))

const TopToolbarStyled = styled(TopToolbar)(({ theme }) => ({
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    backgroundColor: 'transparent',
  },
}))

const TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
  SHOW: 'show',
  EXPORT: 'export',
  LIST: 'list',
}

const TitleStyled = styled(Typography)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

export const ListActions = ({
  title = '',
  hasCreate = true,
  hasExport = true,
  hasPreview = false,
  hasEdit = false,
  hasShow = false,
  hasList = false,
  hasBackButton = false,
  resources = {},
  filters = [],
  menuItems = [],
}: IListActions): JSX.Element => {
  const translate = useTranslate()
  const theme = useTheme()
  const isSmUP = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const resource = useResourceContext()
  const record = useRecordContext()
  const createPath = useCreatePath()
  const { onExport } = useExport()
  const { previewPath } = usePreview()

  const handleClick = useCallback(
    (resource: string, type: string, id?: string | number) => () => {
      const resourceSelected = resources[type] || resource
      const path = createPath({ resource: resourceSelected, id, type })
      navigate(path)
    },
    [resources, createPath, navigate],
  )

  const filteredMenuItems: IMenuItem[] = [
    {
      label: translate('ra.action.create'),
      icon: <ContentAdd />,
      onClick: handleClick(resource, TYPES.CREATE),
      button: <CreateButton variant='contained' resource={resources[TYPES.CREATE]} key={0} />,
      visible: hasCreate,
    },
    {
      label: translate('ra.action.export'),
      icon: <DownloadIcon />,
      onClick: onExport,
      button: <ExportButton variant='contained' resource={resources[TYPES.EXPORT]} key={1} />,
      visible: hasExport,
      // visible: true,
    },
    {
      label: translate('ra.action.preview'),
      icon: <LaunchRoundedIcon />,
      onClick: (): void => {
        navigate(previewPath)
      },
      button: <PreviewButton variant='contained' key={2} />,
      visible: hasPreview,
    },
    {
      label: translate('ra.action.show'),
      icon: <ImageEye />,
      onClick: handleClick(resource, TYPES.SHOW, record?.id),
      button: <ShowButton variant='contained' resource={resources[TYPES.SHOW]} key={3} />,
      visible: hasShow,
    },
    {
      label: translate('ra.action.list'),
      icon: <ActionList />,
      onClick: handleClick(resource, TYPES.LIST),
      button: <ListButton variant='contained' resource={resources[TYPES.LIST]} key={5} />,
      visible: hasList,
    },
    {
      label: translate('ra.action.edit'),
      icon: <ContentCreate />,
      onClick: handleClick(resource, TYPES.EDIT, record?.id),
      button: <EditButton variant='contained' resource={resources[TYPES.EDIT]} key={4} />,
      visible: hasEdit,
    },
    ...menuItems,
  ].filter(({ visible }) => visible)
  const desktopMenu = <>{filteredMenuItems.map(({ button }) => button)}</>
  const mobileMenu = (
    <>
      <MenuIconButton menuItems={filteredMenuItems} />
    </>
  )

  return (
    <TopToolbarContainer>
      {title ? (
        <TitleStyled variant='h4' gutterBottom>
          {hasBackButton && <BackIconButton />}
          {title}
        </TitleStyled>
      ) : (
        hasBackButton && <BackIconButton />
      )}

      {filters.length > 0 ? (
        <FilterForm filters={filters} className='RaFilterForm-root' />
      ) : (
        <Box flexGrow={1} />
      )}
      <TopToolbarStyled>
        {filters.length > 0 && <FilterButton filters={filters} />}

        {isSmUP ? desktopMenu : menuItems.length > 0 ? mobileMenu : null}
      </TopToolbarStyled>
    </TopToolbarContainer>
  )
}
